/** Locked user paste. Canonical: docs/canonical/product/receipt-email-forwarding.md */
export const RECEIPT_FILTER_GMAIL_PASTE =
  'Ordered OR Shipped OR Delivered OR receipt OR invoice OR "order confirmation" OR "e-receipt" OR "order receipt"';

export const RECEIPT_FILTER_GMAIL_NEWER_THAN = 'newer_than:730d';

export const RECEIPT_FILTER_GMAIL_LABEL = 'orders';

export function gmailAgentPrompt(opts?: { address?: string | null }): string {
  const address = opts?.address?.trim()
    || 'the forwarding address shown in MoneySkyline Settings → Receipt email';
  return [
    'You have access to my Gmail (API, MCP, and/or browser). Set up MoneySkyline receipt forwarding on the Gmail account you are connected to. Use tools or the Gmail / Takeout UI. Do not delete, trash, mark spam, or archive. Do not send mail except Forward of matching order threads to the forwarding address. Do not enable “Forward a copy of incoming mail to” — that forwards everything. Do not put newer_than, before:, or after: on the saved filter. Do not forward Google’s Takeout email to the receipt address.',
    '',
    `Forwarding address (exact): ${address}`,
    `Subject search (exact; paste in Subject only; leave From and Has the words empty): ${RECEIPT_FILTER_GMAIL_PASTE}`,
    `Label name (exact): ${RECEIPT_FILTER_GMAIL_LABEL}`,
    '',
    `1. Create a Gmail label named exactly ${RECEIPT_FILTER_GMAIL_LABEL} if it does not exist.`,
    `2. Add a forwarding address: Gmail Settings (gear) → See all settings → Forwarding and POP/IMAP → Add a forwarding address → paste the forwarding address → Next → Proceed. Leave Forwarding as Disable forwarding.`,
    '3. Gmail sends the confirmation to that receipt address, not this inbox. Stop and tell me to open MoneySkyline Settings → Receipt email, confirm the code or link, then reply “confirmed”. Do not invent a code. Do not search this inbox for the confirm mail.',
    `4. After I confirm: create a filter. Subject = the subject search. From empty. Has the words empty. No date operator. Actions: Forward it to the verified address; Apply label ${RECEIPT_FILTER_GMAIL_LABEL}; Also apply filter to matching conversations. Do not check Delete it. Keep Gmail’s copy.`,
    `5. Search all mail (including archived) with this exact query:`,
    `${RECEIPT_FILTER_GMAIL_NEWER_THAN} ${RECEIPT_FILTER_GMAIL_PASTE}`,
    `   Apply the ${RECEIPT_FILTER_GMAIL_LABEL} label to every matching thread. Paginate until the search has no unlabeled matches left.`,
    `6. Search: label:${RECEIPT_FILTER_GMAIL_LABEL} older_than:730d`,
    `   Remove the ${RECEIPT_FILTER_GMAIL_LABEL} label from every thread in that result. Paginate until none remain.`,
    `7. Past mail: the saved filter will not forward old threads. If label:${RECEIPT_FILTER_GMAIL_LABEL} is a small set, open each matching thread and Forward it to the forwarding address. Keep Gmail’s copy. Do not Delete. That sends those receipts to MoneySkyline. Skip Takeout.`,
    `8. Only if there are many matching threads and I want a zip to keep: request Google Takeout for this same Google account: open https://takeout.google.com — Deselect all → tick Mail only. If All data included is missing, untick Mail and tick Mail again. Mail row → All data included (not Multiple formats) → uncheck Include all messages in Mail → tick ${RECEIPT_FILTER_GMAIL_LABEL} only → OK. Next step → Export once, .zip, 2GB, Send download link via email → Create export. I will download the zip. Takeout does not send mail to MoneySkyline. Do not forward Google’s email to the receipt address.`,
    `9. Reply with: forwarding address added (yes/no), confirmation waiting or done, filter created (yes/no), threads labeled, threads unlabeled, remaining count for label:${RECEIPT_FILTER_GMAIL_LABEL}, past threads forwarded (count or skipped), Takeout requested (yes/no/skipped), and anything you could not do.`,
  ].join('\n');
}

export const RECEIPT_FILTER_SUBJECT_TOKENS = [
  'Ordered',
  'Shipped',
  'Delivered',
  'receipt',
  'invoice',
  'order confirmation',
  'e-receipt',
  'order receipt',
] as const;

const RECEIPT_FILTER_GMAIL_CREATE_HASH =
  `#create-filter/subject=${encodeURIComponent(RECEIPT_FILTER_GMAIL_PASTE)}`;

const RECEIPT_FILTER_GMAIL_SEARCH_HASH =
  `#search/${encodeURIComponent(`${RECEIPT_FILTER_GMAIL_NEWER_THAN} ${RECEIPT_FILTER_GMAIL_PASTE}`).replace(/%20/g, '+')}`;

export function gmailDeepLink(
  hash: string,
  account?: { authuser?: string | null; index?: number },
): string {
  const email = account?.authuser?.trim();
  if (email) {
    return `https://mail.google.com/mail/?authuser=${encodeURIComponent(email)}${hash}`;
  }
  const index = account?.index ?? 0;
  return `https://mail.google.com/mail/u/${index}/${hash}`;
}

export function gmailCreateFilterUrl(account?: { authuser?: string | null; index?: number }): string {
  return gmailDeepLink(RECEIPT_FILTER_GMAIL_CREATE_HASH, account);
}

export function gmailSearchUrl(account?: { authuser?: string | null; index?: number }): string {
  return gmailDeepLink(RECEIPT_FILTER_GMAIL_SEARCH_HASH, account);
}

export function gmailFromEmails(
  rows: Array<{ host: string; from_email: string | null }> | undefined,
): string[] {
  const seen = new Set<string>();
  const emails: string[] = [];
  for (const row of rows ?? []) {
    if (row.host !== 'gmail' || !row.from_email) continue;
    const key = row.from_email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push(row.from_email);
  }
  return emails;
}

export function gmailTakeoutUrl(account?: { authuser?: string | null; index?: number }): string {
  const email = account?.authuser?.trim();
  if (email) {
    return `https://takeout.google.com/?authuser=${encodeURIComponent(email)}`;
  }
  return 'https://takeout.google.com/';
}

export const RECEIPT_FILTER_GMAIL_CREATE_URL = gmailCreateFilterUrl({ index: 0 });
export const RECEIPT_FILTER_GMAIL_SEARCH_URL = gmailSearchUrl({ index: 0 });
export const RECEIPT_FILTER_GMAIL_TAKEOUT_URL = gmailTakeoutUrl();

export const RECEIPT_FILTER_GMAIL_HELP_URL = 'https://support.google.com/mail/answer/10957';
export const RECEIPT_FILTER_GMAIL_TAKEOUT_HELP_URL = 'https://support.google.com/accounts/answer/3024190';
export const RECEIPT_FILTER_ICLOUD_HELP_URL =
  'https://support.apple.com/guide/icloud/set-up-filtering-rules-mm6b1a3f8a/icloud';
export const RECEIPT_FILTER_OUTLOOK_HELP_URL =
  'https://support.microsoft.com/en-us/office/use-inbox-rules-in-outlook-com-or-outlook-on-the-web-4b094371-a5d7-49bd-8b1b-4e4896a7cc5d';
export const RECEIPT_FILTER_ICLOUD_MAIL_URL = 'https://www.icloud.com/mail';
export const RECEIPT_FILTER_OUTLOOK_RULES_URL = 'https://outlook.live.com/mail/0/options/mail/rules';
